// DankaiFormV4.jsx
import React from 'react';
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import ExpansionPanelActions from '@material-ui/core/ExpansionPanelActions';
import { t } from 'ttag';
import useReactRouter from 'use-react-router';
import { Add as AddIcon, Delete as DeleteIcon, Save as SaveIcon, Undo as UndoIcon } from '@material-ui/icons';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { useApolloClient } from '@apollo/react-hooks';

import { MyClubCApi } from '../../../api';
import Loading from '../../../components/Loading';
import RichTextEditor, { stripHtmlContent } from '../../../components/RichTextEditor';
import useNotifier from '../../../components/Messages/hooks';
import { ERROR_MESSAGES } from '../../../utils/errors';
import useUser from '../../../auth/hooks';
import { getBasePath } from '../../../utils/urls';
import { convertToYouTubeEmbed, YOUTUBE_EMBED_URL_PATTERN } from '../../../utils/youtube';

// Утиліти
const clone = (x) => JSON.parse(JSON.stringify(x));
const moveInArray = (arr, from, to) => {
  const next = arr.slice();
  const [it] = next.splice(from, 1);
  next.splice(to, 0, it);
  return next;
};

// Початкові значення
const extractItemNamePreview = (value = '') => {
  if (!value) {
    return '';
  }

  const normalized = value
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|ul|ol)>/gi, '\n')
    .replace(/<li[^>]*>/gi, '\n');

  for (const line of normalized.split('\n')) {
    const cleaned = stripHtmlContent(line).trim();
    if (cleaned) {
      return cleaned;
    }
  }

  return '';
};

const emptyItem = () => ({
  name: '',
  link: '',
  description: '',
  tip: ''
});
const emptySection = () => ({
  name: '',
  description: '',
  items: [emptyItem()]
});
const isItemBlank = (item = {}) => {
  const name = stripHtmlContent(item.name || '');
  const link = (item.link || '').trim();
  const description = stripHtmlContent(item.description || '');
  const tip = (item.tip || '').trim();
  return !name && !link && !description && !tip;
};
const initialForm = {
  name: '',
  label: '',
  theme: '',
  criteria: '',
  sections: [emptySection()]
};

export const DankaiForm = ({ onSave, onCancel }) => {
  const { user } = useUser();
  const ctxName = getBasePath(user)
  const { match, history } = useReactRouter();
  const client = useApolloClient();
  const api = React.useMemo(() => new MyClubCApi(client), [client]);
  const pushMessage = useNotifier();

  const dankaiType = match.params.type || '';
  const dankaiIdParam = match.params.id || '';
  const dankaiIdFromRoute = Number(dankaiIdParam);
  const hasValidDankaiId = Number.isFinite(dankaiIdFromRoute) && !Number.isNaN(dankaiIdFromRoute);

  const [form, setForm] = React.useState(initialForm);
  const [errors, setErrors] = React.useState({});
  const [dankaiId, setDankaiId] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [expandedSections, setExpandedSections] = React.useState([true]);
  const [expandedItems, setExpandedItems] = React.useState({ 0: [true] });
  const [showConfirm, setShowConfirm] = React.useState(false);
  const [showSaveConfirm, setShowSaveConfirm] = React.useState(false);
  const [isDirty, setIsDirty] = React.useState(false);
  const initialFormRef = React.useRef(clone(initialForm));

  const TYPE_TAB_MAP = React.useMemo(() => ({
    kumite: 0,
    kata: 1,
  }), []);

  const normalizeItem = React.useCallback((item = {}) => ({
    name: item.name || '',
    link: item.link || '',
    description: item.description || '',
    tip: item.tip || '',
  }), []);

  const normalizeSection = React.useCallback(
    (section = {}) => ({
      name: section.name || '',
      description: section.description || '',
      items:
        section.items && section.items.length
          ? section.items.map((it) => normalizeItem(it))
          : [emptyItem()],
    }),
    [normalizeItem]
  );

  const serializeItem = React.useCallback((item = {}) => ({
    name: item.name,
    description: item.description,
    tip: item.tip,
    link: item.link ? convertToYouTubeEmbed(item.link) : item.link,
  }), []);

const serializeSection = React.useCallback(
    (section) => ({
      name: section.name,
      description: section.description,
      items: (section.items || [])
        .filter((it) => !isItemBlank(it))
        .map((it) => serializeItem(it)),
    }),
    [serializeItem]
  );

  React.useEffect(() => {
    let isCancelled = false;

    const loadData = async () => {
      if (!dankaiType || !hasValidDankaiId) {
        const initial = clone(initialForm);
        setForm(initial);
        setDankaiId(null);
        setExpandedSections([true]);
        setExpandedItems({ 0: [true] });
        initialFormRef.current = clone(initial);
        setIsDirty(false);
        setLoading(false);
        if (!hasValidDankaiId) {
          pushMessage({ text: t`Невірний ідентифікатор данкаю` });
          history.goBack();
        }
        return;
      }

      setLoading(true);
      setErrors({});
      try {
        const detail = await api.getDankai({
          dankaiId: dankaiIdFromRoute,
        });

        if (isCancelled) {
          return;
        }

        if (!detail) {
          pushMessage({ text: t`Данкай не знайдено` });
          history.goBack();
          return;
        }

        const normalizedSections =
          detail.sections && detail.sections.length
            ? detail.sections.map((section) => normalizeSection(section))
            : [emptySection()];

        const nextForm = {
          name: detail.name || '',
          label: detail.label || '',
          theme: detail.theme || '',
          criteria: detail.criteria || '',
          sections: normalizedSections,
        };

        setDankaiId(detail.id);
        setForm(nextForm);
        setExpandedSections(
          normalizedSections.map((_, index) => index === 0)
        );
        const itemsExpanded = {};
        normalizedSections.forEach((section, index) => {
          itemsExpanded[index] = section.items.map((__, itemIndex) => itemIndex === 0);
          if (itemsExpanded[index].length === 0) {
            itemsExpanded[index] = [true];
          }
        });
        setExpandedItems(itemsExpanded);
        initialFormRef.current = clone(nextForm);
        setIsDirty(false);
      } catch (error) {
        if (!isCancelled) {
          // eslint-disable-next-line no-console
          console.error('Failed to load dankai', error);
          pushMessage({ text: t`Не вдалося завантажити данкай` });
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isCancelled = true;
    };
  }, [api, dankaiIdFromRoute, dankaiType, hasValidDankaiId, history, normalizeSection, pushMessage]);

  React.useEffect(() => {
    if (loading) {
      return;
    }
    const initialSerialized = JSON.stringify(initialFormRef.current);
    const currentSerialized = JSON.stringify(form);
    setIsDirty(initialSerialized !== currentSerialized);
  }, [form, loading]);

  const navigateToList = React.useCallback(() => {
    const tab = TYPE_TAB_MAP[dankaiType] ?? 0;
    history.push(`/${ctxName}/dankai?tab=${tab}`);
  }, [TYPE_TAB_MAP, dankaiType, history]);

  // --------- Змінa полів верхнього рівня ----------
  const setField = (key, value) => {
    setForm((f) => {
      const next = { ...f, [key]: value };
      return next;
    });
    setIsDirty(true);
  };

  // --------- Операції з розділами ----------
  const addSection = () =>
    setForm((f) => {
      const next = clone(f);
      next.sections.push(emptySection());
      const newIndex = next.sections.length - 1;
      setExpandedSections((prev) => [...prev.map(Boolean), true]);
      setExpandedItems((prev) => ({
        ...prev,
        [newIndex]: [true],
      }));
      setIsDirty(true);
      return next;
    });

  const removeSection = (si) =>
    setForm((f) => {
      const next = clone(f);
      if (next.sections.length <= 1) {
        const fallback = emptySection();
        next.sections = [fallback];
        setExpandedSections([true]);
        setExpandedItems({ 0: [true] });
        setIsDirty(true);
        return next;
      }

      next.sections.splice(si, 1);
      setExpandedSections((prev) => {
        const updated = prev.filter((_, index) => index !== si);
        if (updated.length === 0) {
          return [true];
        }
        if (!updated.some(Boolean)) {
          updated[0] = true;
        }
        return updated;
      });
      setExpandedItems((prev) => {
        const map = {};
        next.sections.forEach((section, index) => {
          const prevIndex = index >= si ? index + 1 : index;
          const prevValues = prev[prevIndex] || [];
          let normalized = prevValues.slice(0, section.items.length);
          while (normalized.length < section.items.length) {
            normalized.push(false);
          }
          if (normalized.length === 0) {
            normalized = [true];
          }
          if (!normalized.some(Boolean)) {
            normalized[0] = true;
          }
          map[index] = normalized;
        });
        return map;
      });
      setIsDirty(true);
      return next;
    });
  const setSectionField = (si, key, value) =>
    setForm((f) => {
      const next = clone(f);
      next.sections[si][key] = value;
      setIsDirty(true);
      return next;
    });

  // --------- Операції з пунктами (завданнями) ----------
  const addItem = (si) =>
    setForm((f) => {
      const next = clone(f);
      next.sections[si].items.push(emptyItem());
      setExpandedSections((prev) => {
        const updated = prev.slice();
        updated[si] = true;
        return updated;
      });
      setExpandedItems((prev) => {
        const current = prev[si] ? prev[si].slice() : [];
        const updated = current.map(Boolean);
        updated.push(true);
        return {
          ...prev,
          [si]: updated.length ? updated : [true],
        };
      });
      setIsDirty(true);
      return next;
    });
  const removeItem = (si, ii) =>
    setForm((f) => {
      const next = clone(f);
      next.sections[si].items.splice(ii, 1);
      if (next.sections[si].items.length === 0) {
        next.sections[si].items.push(emptyItem());
        setExpandedItems((prev) => ({ ...prev, [si]: [true] }));
      } else {
        const maxIndex = next.sections[si].items.length - 1;
        setExpandedItems((prev) => {
          const current = prev[si] ? prev[si].slice() : [];
          const updated = current.filter((_, index) => index !== ii);
          if (updated.length === 0) {
            updated.push(true);
          }
          // Ensure at least one open item
          if (!updated.some(Boolean)) {
            updated[Math.min(updated.length - 1, maxIndex)] = true;
          }
          return {
            ...prev,
            [si]: updated,
          };
        });
      }
      setIsDirty(true);
      return next;
    });

  const setItemField = (si, ii, key, value) =>
    setForm((f) => {
      const next = clone(f);
      next.sections[si].items[ii][key] = value;
      setIsDirty(true);
      return next;
    });

  const duplicateItem = (si, ii) =>
    setForm((f) => {
      const next = clone(f);
      const copy = clone(next.sections[si].items[ii]);
      next.sections[si].items.splice(ii + 1, 0, copy);
      setExpandedSections((prev) => {
        const updated = prev.slice();
        updated[si] = true;
        return updated;
      });
      setExpandedItems((prev) => {
        const current = prev[si] ? prev[si].slice() : [];
        const updated = current.map(Boolean);
        updated.splice(ii + 1, 0, true);
        return {
          ...prev,
          [si]: updated,
        };
      });
      setIsDirty(true);
      return next;
    });

  // --------- Збереження ----------
  const handleCancelClick = () => {
    if (saving || loading) {
      return;
    }
    if (isDirty) {
      setShowConfirm(true);
      return;
    }
    handleCancel();
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
      return;
    }
    history.goBack();
  };

  const handleSave = async () => {
    if (!dankaiId) {
      pushMessage({ text: t`Данкай не знайдено` });
      return false;
    }

    const validationErrors = {};
    form.sections.forEach((section, si) => {
      const sectionName = (section.name || '').trim();
      if (!sectionName) {
        validationErrors[`section-${si}-name`] = ERROR_MESSAGES.required;
      }

      (section.items || []).forEach((item, ii) => {
        if (isItemBlank(item)) {
          return;
        }

        const itemName = stripHtmlContent(item.name || '');
        if (!itemName) {
          validationErrors[`section-${si}-item-${ii}-name`] = ERROR_MESSAGES.required;
        }

        const link = (item.link || '').trim();
        if (link) {
          // Спробуємо конвертувати посилання
          const convertedLink = convertToYouTubeEmbed(link);
          if (!YOUTUBE_EMBED_URL_PATTERN.test(convertedLink)) {
            validationErrors[`section-${si}-item-${ii}-link`] = t`Вкажіть правильне посилання на YouTube відео`;
          }
        }
      });
    });

    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      pushMessage({ text: t`Перевірте правильність заповнення форми` });
      return false;
    }

    const payload = {
      name: form.name,
      label: form.label,
      theme: form.theme,
      criteria: form.criteria,
      type: dankaiType,
      sections: form.sections.map((section) => serializeSection(section)),
    };

    setSaving(true);
    setErrors({});
    try {
      const response = await api.updateDankai({
        id: dankaiId,
        inputData: payload,
      });

      if (response?.errors && response.errors.length) {
        const nextErrors = {};
        response.errors.forEach((err) => {
          if (!err) {
            return;
          }
          const fieldKey = err.field || 'form';
          nextErrors[fieldKey] = err.messages && err.messages.length ? err.messages[0] : '';
        });
        setErrors(nextErrors);
        pushMessage({
          text: nextErrors.form || t`Не вдалося зберегти дані`,
        });
        return false;
      }

      if (onSave) {
        onSave(response?.dankai || payload);
      }

      pushMessage({ text: t`Зміни збережено` });

      if (response?.dankai) {
        const updatedForm = {
          name: response.dankai.name || '',
          label: response.dankai.label || '',
          theme: response.dankai.theme || '',
          criteria: response.dankai.criteria || '',
          sections:
            response.dankai.sections && response.dankai.sections.length
              ? response.dankai.sections.map((section) => normalizeSection(section))
              : [emptySection()],
        };
        setForm(updatedForm);
        initialFormRef.current = clone(updatedForm);
        setIsDirty(false);
        setExpandedSections(
          updatedForm.sections.map((_, index) => index === 0)
        );
        const updatedItemsExpanded = {};
        updatedForm.sections.forEach((section, index) => {
          updatedItemsExpanded[index] = (section.items || []).map((__, itemIndex) => itemIndex === 0);
          if (updatedItemsExpanded[index].length === 0) {
            updatedItemsExpanded[index] = [true];
          }
        });
        setExpandedItems(updatedItemsExpanded);
      } else {
        initialFormRef.current = clone(form);
        setIsDirty(false);
        setExpandedSections((prev) => {
          if (prev.length === form.sections.length) {
            return prev;
          }
          return form.sections.map((_, index) => prev[index] ?? (index === 0));
        });
        setExpandedItems((prev) => {
          const map = { ...prev };
          form.sections.forEach((section, index) => {
            const prevValues = map[index] ? map[index].slice() : [];
            let normalized = section.items.map((__, itemIndex) => prevValues[itemIndex] ?? (itemIndex === 0));
            if (normalized.length === 0) {
              normalized = [true];
            }
            if (!normalized.some(Boolean)) {
              normalized[0] = true;
            }
            map[index] = normalized;
          });
          return map;
        });
      }

      return true;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to update dankai', error);
      pushMessage({ text: t`Не вдалося зберегти дані` });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmYes = async () => {
    setShowConfirm(false);
    const success = await handleSave();
    if (success) {
      navigateToList();
    }
  };

  const handleConfirmNo = () => {
    setShowConfirm(false);
    handleCancel();
  };

  const handleSaveConfirmYes = async () => {
    setShowSaveConfirm(false);
    const success = await handleSave();
    if (success) {
      navigateToList();
    }
  };

  const handleSaveConfirmNo = () => {
    setShowSaveConfirm(false);
  };

  const handleSaveClick = async () => {
    if (saving || loading) {
      return;
    }
    if (!isDirty) {
      const success = await handleSave();
      if (success) {
        navigateToList();
      }
      return;
    }
    setShowSaveConfirm(true);
  };

  // --------- Drag & Drop ----------
  const onDragEnd = (result) => {
    const { source, destination, type } = result;
    if (!destination) return;

    // Sections reorder
    if (type === 'SECTIONS' || source.droppableId === 'sections') {
      const from = source.index;
      const to = destination.index;
      if (from === to) return;
      setForm((f) => {
        const next = clone(f);
        next.sections = moveInArray(next.sections, from, to);
        setExpandedSections((prev) => {
          const prevSafe = prev.slice();
          while (prevSafe.length < next.sections.length) {
            prevSafe.push(false);
          }
          if (prevSafe.length > next.sections.length) {
            prevSafe.length = next.sections.length;
          }
          const updated = moveInArray(prevSafe, from, to);
          if (!updated.some(Boolean)) {
            updated[to] = true;
          }
          return updated;
        });
        setExpandedItems((prev) => {
          const prevList = Object.keys(prev)
            .sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
            .map((key) => prev[parseInt(key, 10)] || []);
          while (prevList.length < next.sections.length) {
            prevList.push([]);
          }
          if (prevList.length > next.sections.length) {
            prevList.length = next.sections.length;
          }
          const moved = moveInArray(prevList, from, to);
          const map = {};
          moved.forEach((value, index) => {
            const limit = next.sections[index].items.length;
            const normalized = (value || []).slice(0, limit);
            while (normalized.length < limit) {
              normalized.push(false);
            }
            if (normalized.length === 0) {
              normalized.push(true);
            }
            if (!normalized.some(Boolean)) {
              normalized[0] = true;
            }
            map[index] = normalized;
          });
          return map;
        });
        setIsDirty(true);
        return next;
      });
      return;
    }

    // Items reorder (supports moving between sections)
    if (type === 'ITEM' || source.droppableId.startsWith('items-')) {
      const fromSi = parseInt(source.droppableId.split('-')[1], 10);
      const toSi = parseInt(destination.droppableId.split('-')[1], 10);
      const fromIndex = source.index;
      const toIndex = destination.index;
      if (fromSi === toSi && fromIndex === toIndex) return;
      setForm((f) => {
        const next = clone(f);
        const [moved] = next.sections[fromSi].items.splice(fromIndex, 1);
        if (next.sections[fromSi].items.length === 0) next.sections[fromSi].items.push(emptyItem());
        next.sections[toSi].items.splice(toIndex, 0, moved);
        setExpandedItems((prev) => {
          const map = { ...prev };
          const fromItems = map[fromSi] ? map[fromSi].slice() : [];
          const toItems = map[toSi] ? map[toSi].slice() : [];

          if (fromItems.length === 0) {
            fromItems.push(true);
          }
          if (toItems.length === 0) {
            toItems.push(true);
          }

          const [fromExpanded] = fromItems.splice(fromIndex, 1);
          toItems.splice(toIndex, 0, fromExpanded || true);

          if (!fromItems.some(Boolean)) {
            fromItems[Math.max(0, Math.min(fromIndex, fromItems.length - 1))] = true;
          }
          if (!toItems.some(Boolean)) {
            toItems[Math.min(toIndex, toItems.length - 1)] = true;
          }

          map[fromSi] = fromItems.length ? fromItems : [true];
          map[toSi] = toItems.length ? toItems : [true];
          return map;
        });
        setIsDirty(true);
        return next;
      });
      return;
    }
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <Box minHeight="100vh">
      <Container maxWidth="lg">
        <Box
          display="flex"
          flexDirection={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
        >
          <Typography variant="h4" component="h1">
            {form.name}
          </Typography>
        </Box>
        <Paper elevation={0} style={{ padding: 16, marginBottom: 16 }}>
          <Grid container spacing={2} alignItems="flex-start">
            {/* <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Назва данкаю"
                value={form.name}
                variant="outlined"
                size="small"
                disabled
              />
            </Grid> */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Тема"
                value={form.theme}
                onChange={(e) => setField('theme', e.target.value)}
                variant="outlined"
                multiline
                rows={2}
                placeholder="Тема"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Критерії"
                value={form.criteria}
                onChange={(e) => setField('criteria', e.target.value)}
                variant="outlined"
                multiline
                rows={2}
                placeholder="Критерії"
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Розділи */}
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="sections" direction="vertical" type="SECTIONS">
            {(dropProvided) => (
              <div ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
                {form.sections.map((section, si) => (
                  <Draggable key={`section-${si}`} draggableId={`section-${si}`} index={si}>
                    {(dragProvided, dragSnapshot) => (
                      <div
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        style={{
                          ...dragProvided.draggableProps.style,
                          marginBottom: 16,
                          borderRadius: 4,
                          boxShadow: dragSnapshot.isDragging ? '0 4px 12px rgba(0,0,0,0.15)' : 'none',
                          background: dragSnapshot.isDragging ? '#fff' : 'transparent'
                        }}
                      >
                        <ExpansionPanel
                          expanded={expandedSections[si] ?? false}
                          onChange={(_, isExpanded) =>
                            setExpandedSections((prev) => {
                              const updated = prev.slice();
                              updated[si] = isExpanded;
                              return updated;
                            })
                          }
                        >
                          <ExpansionPanelSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls={`section-${si}-content`}
                            id={`section-${si}-header`}
                          >
                            <Box display="flex" alignItems="center" width="100%">
                              <Tooltip title="Перетягнути розділ">
                                <IconButton
                                  edge="start"
                                  size="small"
                                  aria-label="drag section"
                                  {...dragProvided.dragHandleProps}
                                  style={{ cursor: dragSnapshot.isDragging ? 'grabbing' : 'grab' }}
                                >
                                  <DragIndicatorIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Box ml={1}>
                                <Typography variant="subtitle1">
                                  <Box component="span" fontWeight={600}>
                                    {t`Розділ ${si + 1}:`}
                                  </Box>
                                  {` ${section.name}`}
                                </Typography>
                              </Box>
                            </Box>
                          </ExpansionPanelSummary>
                          <ExpansionPanelDetails>
                            <Paper elevation={0} style={{ padding: 16, width: '100%' }}>
                              <Grid container spacing={2} alignItems="flex-start">
                                <Grid item xs={12} md={6}>
                                  <TextField
                                    fullWidth
                                    label="Назва розділу"
                                    value={section.name}
                                    onChange={(e) => setSectionField(si, 'name', e.target.value)}
                                    error={Boolean(errors[`section-${si}-name`])}
                                    helperText={errors[`section-${si}-name`] || ' '}
                                    variant="outlined"
                                    size="small"
                                  />
                                </Grid>
                                <Grid item xs={12}>
                                  <TextField
                                    fullWidth
                                    label="Опис розділу (опціонально)"
                                    placeholder="Опис розділу"
                                    value={section.description}
                                    onChange={(e) => setSectionField(si, 'description', e.target.value)}
                                    multiline
                                    rows={2}
                                    variant="outlined"
                                    size="small"
                                  />
                                </Grid>
                              </Grid>

                              <Box mt={2} />
                              <Droppable droppableId={`items-${si}`} type="ITEM" direction="vertical">
                                {(itemsProvided) => {
                                  const currentExpandedItems = expandedItems[si] || [];
                                  return (
                                    <div ref={itemsProvided.innerRef} {...itemsProvided.droppableProps}>
                                    {section.items.map((item, ii) => (
                                      <Draggable key={`item-${si}-${ii}`} draggableId={`item-${si}-${ii}`} index={ii}>
                                        {(itemProvided, itemSnapshot) => (
                                          <div
                                            ref={itemProvided.innerRef}
                                            {...itemProvided.draggableProps}
                                            style={{
                                              ...itemProvided.draggableProps.style,
                                              marginBottom: 12,
                                              borderRadius: 4,
                                              boxShadow: itemSnapshot.isDragging ? '0 4px 12px rgba(0,0,0,0.1)' : 'none',
                                              background: itemSnapshot.isDragging ? '#fff' : 'transparent'
                                            }}
                                          >
                                            <ExpansionPanel
                                              expanded={currentExpandedItems[ii] ?? false}
                                              onChange={(_, isExpanded) =>
                                                setExpandedItems((prev) => {
                                                  const map = { ...prev };
                                                  const itemsExpanded = map[si]
                                                    ? map[si].slice()
                                                    : Array(section.items.length).fill(false);
                                                  itemsExpanded[ii] = isExpanded;
                                                  if (!itemsExpanded.some(Boolean)) {
                                                    itemsExpanded[ii] = isExpanded;
                                                  }
                                                  map[si] = itemsExpanded;
                                                  return map;
                                                })
                                              }
                                            >
                                              <ExpansionPanelSummary
                                                expandIcon={<ExpandMoreIcon />}
                                                aria-controls={`item-${si}-${ii}-content`}
                                                id={`item-${si}-${ii}-header`}
                                              >
                                                <Box display="flex" alignItems="center" width="100%">
                                                  <Tooltip title="Перетягнути завдання">
                                                    <IconButton
                                                      edge="start"
                                                      size="small"
                                                      aria-label="drag item"
                                                      {...itemProvided.dragHandleProps}
                                                      style={{ cursor: itemSnapshot.isDragging ? 'grabbing' : 'grab' }}
                                                    >
                                                      <DragIndicatorIcon fontSize="small" />
                                                    </IconButton>
                                                  </Tooltip>
                                                  <Box ml={1}>
                                                    <Typography variant="subtitle1">
                                                      <Box component="span" fontWeight={600}>
                                                        {t`Завдання ${ii + 1}:`}
                                                      </Box>
                                                      {(() => { const preview = extractItemNamePreview(item.name); return preview ? ` ${preview}` : ''; })()}
                                                    </Typography>
                                                  </Box>
                                                </Box>
                                              </ExpansionPanelSummary>
                                              <ExpansionPanelDetails>
                                                <Grid container spacing={2} alignItems="flex-start">
                                                  <Grid item xs={12}>
                                                    <RichTextEditor
                                                      label="Опис завдання"
                                                      placeholder="Опис завдання"
                                                      value={item.name}
                                                      onChange={(html) => setItemField(si, ii, 'name', html)}
                                                      error={Boolean(errors[`section-${si}-item-${ii}-name`])}
                                                      helperText={errors[`section-${si}-item-${ii}-name`] || ' '}
                                                      rows={5}
                                                    />
                                                  </Grid>
                                                  <Grid item xs={12}>
                                                    <TextField
                                                      fullWidth
                                                      label="Посилання (опціонально)"
                                                      placeholder="https://youtube.com/watch?v=... або https://youtu.be/... або https://youtube.com/shorts/..."
                                                      value={item.link}
                                                      onChange={(e) => {
                                                        const rawValue = e.target.value;
                                                        const convertedValue = convertToYouTubeEmbed(rawValue);
                                                        setItemField(si, ii, 'link', convertedValue);
                                                      }}
                                                      onPaste={(e) => {
                                                        // Отримуємо дані з буфера обміну
                                                        const clipboardData = e.clipboardData || window.clipboardData;
                                                        if (clipboardData) {
                                                          const pastedText = clipboardData.getData('text');
                                                          if (pastedText) {
                                                            const convertedValue = convertToYouTubeEmbed(pastedText);
                                                            if (pastedText !== convertedValue) {
                                                              // Запобігаємо стандартній поведінці paste
                                                              e.preventDefault();
                                                              setItemField(si, ii, 'link', convertedValue);
                                                            }
                                                          }
                                                        }
                                                      }}
                                                      error={Boolean(errors[`section-${si}-item-${ii}-link`])}
                                                      helperText={errors[`section-${si}-item-${ii}-link`] || ' '}
                                                      variant="outlined"
                                                      size="small"
                                                    />
                                                  </Grid>
                                                  <Grid item xs={12}>
                                                    <TextField
                                                      fullWidth
                                                      label="Додатковий опис (опціонально)"
                                                      placeholder="Додатковий опис (опціонально)"
                                                      value={item.description}
                                                      onChange={(e) => setItemField(si, ii, 'description', e.target.value)}
                                                      multiline
                                                      rows={2}
                                                      variant="outlined"
                                                      size="small"
                                                      helperText=" "
                                                    />
                                                  </Grid>
                                                </Grid>
                                              </ExpansionPanelDetails>
                                              <ExpansionPanelActions>
                                                <Box display="flex" justifyContent="flex-end" width="100%">
                                                  <Box mr={1}>
                                                    <Tooltip title="Дублювати завдання">
                                                      <IconButton
                                                        aria-label="duplicate item"
                                                        onClick={() => duplicateItem(si, ii)}
                                                      >
                                                        <FileCopyIcon fontSize="small" />
                                                      </IconButton>
                                                    </Tooltip>
                                                  </Box>
                                                  <Tooltip title="Видалити завдання">
                                                    <IconButton
                                                      aria-label="delete item"
                                                      color="secondary"
                                                      onClick={() => removeItem(si, ii)}
                                                    >
                                                      <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                  </Tooltip>
                                                </Box>
                                              </ExpansionPanelActions>
                                            </ExpansionPanel>
                                          </div>
                                        )}
                                      </Draggable>
                                    ))}
                                    {itemsProvided.placeholder}
                                  </div>
                                  );
                                }}
                              </Droppable>
                            </Paper>
                          </ExpansionPanelDetails>
                          <ExpansionPanelActions>
                            <Box display="flex" justifyContent="flex-start" width="100%">
                              <Box mr={1}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<AddIcon />}
                                  onClick={() => addItem(si)}
                                >
                                  Додати завдання
                                </Button>
                              </Box>
                              <Button
                                size="small"
                                variant="outlined"
                                color="secondary"
                                startIcon={<DeleteIcon />}
                                onClick={() => removeSection(si)}
                              >
                                Видалити розділ
                              </Button>
                            </Box>
                          </ExpansionPanelActions>
                        </ExpansionPanel>
                      </div>
                    )}
                  </Draggable>
                ))}
                {dropProvided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        <Box mb={10} />

        <Paper
          elevation={3}
          style={{
            position: 'sticky',
            bottom: 0,
            zIndex: 20,
            background: '#fff',
            padding: 12,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            borderTop: '1px solid rgba(0,0,0,0.08)',
          }}
        >
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addSection}
            disabled={saving}
          >
            Додати розділ
          </Button>
          <Box display="flex" alignItems="center">
            <Button
              variant="contained"
              color="default"
              startIcon={<UndoIcon />}
              onClick={handleCancelClick}
              disabled={saving}
            >
              Скасувати
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSaveClick}
              disabled={saving}
              style={{ marginLeft: 12 }}
            >
              Зберегти
            </Button>
          </Box>
        </Paper>
      </Container>

      <Dialog open={showConfirm} onClose={() => setShowConfirm(false)}>
        <DialogTitle>{t`Зберегти зміни?`}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t`Ви маєте незбережені зміни. Зберегти перед виходом?`}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmYes} color="primary">
            {t`Так`}
          </Button>
          <Button onClick={handleConfirmNo} color="primary">
            {t`Ні`}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showSaveConfirm} onClose={() => setShowSaveConfirm(false)}>
        <DialogTitle>{t`Зберегти зміни?`}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t`Ви маєте незбережені зміни. Зберегти перед переходом до списку?`}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSaveConfirmYes} color="primary">
            {t`Так`}
          </Button>
          <Button onClick={handleSaveConfirmNo} color="primary">
            {t`Ні`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
